import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import { Team, Song, SongAnswer, GameState, DifficultyLevel, Round } from '../types';
import { getInitialTokens, calculateScore, insertCardInTimeline } from '../utils/gameLogic';
import { saveTeams, loadTeams, savePlaylist, loadPlaylist, saveLiveState, loadLiveState, LIVE_STATE_KEY, loadPlaylistFromServer, loadTeamsFromServer, syncPlaylistToServer } from '../utils/storage';

const SONGS_PER_ROUND = 10;

interface GameContextType extends GameState {
  // Team management
  addTeam: (name: string, difficulty: DifficultyLevel, startYear: number) => void;
  removeTeam: (id: string) => void;
  updateTeam: (id: string, updates: Partial<Team>) => void;
  clearTeams: () => void;

  // Playlist management
  addSong: (song: Omit<Song, 'id' | 'played' | 'questions'> & { questions?: string[] }) => void;
  removeSong: (id: string) => void;
  selectSong: (id: string) => void;
  updateSong: (songId: string, updates: Partial<Song>) => void;
  updateSongQuestions: (songId: string, questions: string[]) => void;
  clearPlaylist: () => void;

  // Round management (10 songs per round)
  startNewRound: () => void;
  playNextSong: () => void;
  stopCurrentSong: () => void;
  finishRound: () => void;
  
  // Scoring (after round is complete)
  updateSongAnswer: (teamId: string, songId: string, answer: Partial<SongAnswer>) => void;
  calculateRoundResults: () => void;
  
  // Legacy compatibility
  startRound: () => void;
  stopRound: () => void;
  updateRoundAnswer: (teamId: string, answer: Partial<SongAnswer>) => void;
  calculateResults: () => void;
  nextRound: () => void;
  resetResults: () => void;

  // Token management
  spendToken: (teamId: string) => void;
  earnToken: (teamId: string) => void;
  buyCard: (teamId: string, year: number, title?: string, artist?: string) => void;

  // Audio control
  setIsPlaying: (playing: boolean) => void;
  setIsPaused: (paused: boolean) => void;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

export const GameProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<GameState>({
    teams: [],
    playlist: [],
    currentSongIndex: null,
    currentSong: null,
    roundNumber: 0,
    currentRound: null,
    roundAnswers: {},
    showResults: false,
    isPlaying: false,
    isPaused: false,
  });

  // Load teams from localStorage on mount, then try server
  useEffect(() => {
    const loadInitialTeams = async () => {
      // Helper to ensure teams have timeline property (migration for old data)
      const migrateTeams = (teams: Team[]): Team[] => teams.map(team => ({
        ...team,
        timeline: team.timeline || [{ year: 1990, isStart: true }],
      }));
      
      // First try localStorage for immediate data
      const savedTeams = loadTeams();
      if (savedTeams && savedTeams.length > 0) {
        setState(prev => ({ ...prev, teams: migrateTeams(savedTeams) }));
      }
      
      // Then try server for persistent data (might be more up-to-date)
      const serverTeams = await loadTeamsFromServer();
      if (serverTeams && serverTeams.length > 0) {
        setState(prev => ({ ...prev, teams: migrateTeams(serverTeams) }));
      }
    };
    loadInitialTeams();
  }, []);

  // Load playlist from localStorage on mount, then try server
  useEffect(() => {
    const loadInitialPlaylist = async () => {
      // First try localStorage for immediate data
      const savedPlaylist = loadPlaylist();
      if (savedPlaylist && savedPlaylist.length > 0) {
        setState(prev => ({ ...prev, playlist: savedPlaylist }));
      }
      
      // Then try server for persistent data (might be more up-to-date)
      const serverPlaylist = await loadPlaylistFromServer();
      if (serverPlaylist && serverPlaylist.length > 0) {
        setState(prev => ({ ...prev, playlist: serverPlaylist }));
      }
    };
    loadInitialPlaylist();
  }, []);

  // Save teams to localStorage when they change
  useEffect(() => {
    if (state.teams.length > 0) {
      saveTeams(state.teams);
    }
  }, [state.teams]);

  // Save playlist to localStorage when it changes
  useEffect(() => {
    savePlaylist(state.playlist);
  }, [state.playlist]);

  // Track if we're currently receiving an update from another window
  const isReceivingUpdate = useRef(false);
  // Track if this window is the "controller" (Admin page)
  const isController = useRef(false);

  // Broadcast live state changes to other windows (only from controller)
  useEffect(() => {
    // Skip saving if we just received an update from another window
    if (isReceivingUpdate.current) {
      return;
    }
    
    // Only broadcast if this window initiated the state change
    // Check if we're on a page that controls game state
    const pathname = window.location.pathname;
    if (pathname.includes('admin') || pathname.includes('scoring')) {
      isController.current = true;
      saveLiveState({
        isPlaying: state.isPlaying,
        isPaused: state.isPaused,
        showResults: state.showResults,
        roundNumber: state.roundNumber,
        currentSong: state.currentSong,
        timestamp: Date.now(),
      });
    }
  }, [state.isPlaying, state.isPaused, state.showResults, state.roundNumber, state.currentSong]);

  // Listen for live state changes from other windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Only receive updates if we're NOT the controller
      if (e.key === LIVE_STATE_KEY && e.newValue && !isController.current) {
        try {
          isReceivingUpdate.current = true;
          const liveState = JSON.parse(e.newValue);
          setState(prev => ({
            ...prev,
            isPlaying: liveState.isPlaying,
            isPaused: liveState.isPaused,
            showResults: liveState.showResults,
            roundNumber: liveState.roundNumber,
            currentSong: liveState.currentSong,
          }));
        } catch (error) {
          console.warn('Could not parse live state from storage event:', error);
        }
        // Reset the flag after a short delay
        setTimeout(() => {
          isReceivingUpdate.current = false;
        }, 100);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Load live state on mount (for Display page opening after Admin)
  useEffect(() => {
    const pathname = window.location.pathname;
    // Only load live state if we're NOT on a controller page
    if (!pathname.includes('admin') && !pathname.includes('scoring')) {
      const liveState = loadLiveState();
      if (liveState) {
        setState(prev => ({
          ...prev,
          isPlaying: liveState.isPlaying,
          isPaused: liveState.isPaused,
          showResults: liveState.showResults,
          roundNumber: liveState.roundNumber,
          currentSong: liveState.currentSong,
        }));
      }
    } else {
      isController.current = true;
    }
  }, []);

  // Team management
  const addTeam = (name: string, difficulty: DifficultyLevel, startYear: number) => {
    if (state.teams.length >= 12) return;

    const newTeam: Team = {
      id: `team-${Date.now()}`,
      name,
      difficulty,
      tokens: getInitialTokens(difficulty),
      timeline: [{ year: startYear, isStart: true }],
      score: 0,
    };

    setState(prev => ({
      ...prev,
      teams: [...prev.teams, newTeam],
    }));
  };

  const removeTeam = (id: string) => {
    setState(prev => ({
      ...prev,
      teams: prev.teams.filter(team => team.id !== id),
    }));
  };

  const updateTeam = (id: string, updates: Partial<Team>) => {
    setState(prev => ({
      ...prev,
      teams: prev.teams.map(team =>
        team.id === id ? { ...team, ...updates } : team
      ),
    }));
  };

  const clearTeams = () => {
    setState(prev => ({
      ...prev,
      teams: [],
    }));
    try {
      localStorage.removeItem('hitster-teams');
    } catch (error) {
      console.warn('Could not clear teams from localStorage:', error);
    }
  };

  // Playlist management
  const addSong = (song: Omit<Song, 'id' | 'played' | 'questions'> & { questions?: string[] }) => {
    // Check for duplicates based on title and artist (case-insensitive)
    const isDuplicate = state.playlist.some(
      existing => 
        existing.title.toLowerCase().trim() === song.title.toLowerCase().trim() &&
        existing.artist.toLowerCase().trim() === song.artist.toLowerCase().trim()
    );

    if (isDuplicate) {
      console.log(`⚠️ Duplicate song ignored: ${song.artist} - ${song.title}`);
      return;
    }

    const newSong: Song = {
      ...song,
      id: `song-${Date.now()}`,
      played: false,
      questions: song.questions || [],
    };

    setState(prev => ({
      ...prev,
      playlist: [...prev.playlist, newSong],
    }));
  };

  const removeSong = (id: string) => {
    setState(prev => ({
      ...prev,
      playlist: prev.playlist.filter(song => song.id !== id),
    }));
  };

  const selectSong = (id: string) => {
    const songIndex = state.playlist.findIndex(song => song.id === id);
    const song = state.playlist[songIndex];

    setState(prev => ({
      ...prev,
      currentSongIndex: songIndex,
      currentSong: song || null,
    }));
  };

  const updateSong = (songId: string, updates: Partial<Song>) => {
    setState(prev => ({
      ...prev,
      playlist: prev.playlist.map(song =>
        song.id === songId ? { ...song, ...updates } : song
      ),
      // Update currentSong if it's the one being edited
      currentSong: prev.currentSong?.id === songId
        ? { ...prev.currentSong, ...updates }
        : prev.currentSong,
    }));
  };

  const updateSongQuestions = (songId: string, questions: string[]) => {
    setState(prev => ({
      ...prev,
      playlist: prev.playlist.map(song =>
        song.id === songId ? { ...song, questions } : song
      ),
      // Update currentSong if it's the one being edited
      currentSong: prev.currentSong?.id === songId
        ? { ...prev.currentSong, questions }
        : prev.currentSong,
    }));
  };

  const clearPlaylist = () => {
    setState(prev => ({
      ...prev,
      playlist: [],
      currentSong: null,
      currentSongIndex: null,
    }));
    try {
      localStorage.removeItem('hitster-playlist');
    } catch (error) {
      console.warn('Could not clear playlist from localStorage:', error);
    }
    // Also clear from server
    syncPlaylistToServer([]);
  };

  // Round management (10 songs per round)
  const startNewRound = () => {
    // Get next 10 unplayed songs
    const unplayedSongs = state.playlist.filter(s => !s.played);
    const roundSongs = unplayedSongs.slice(0, SONGS_PER_ROUND);
    
    if (roundSongs.length === 0) return;

    const newRound: Round = {
      roundNumber: state.roundNumber + 1,
      songs: roundSongs,
      isComplete: false,
      currentSongIndex: 0,
    };

    setState(prev => ({
      ...prev,
      roundNumber: prev.roundNumber + 1,
      currentRound: newRound,
      currentSong: roundSongs[0],
      currentSongIndex: 0,
      roundAnswers: {},
      showResults: false,
    }));
  };

  const playNextSong = () => {
    if (!state.currentRound) return;
    
    const nextIndex = state.currentRound.currentSongIndex + 1;
    if (nextIndex >= state.currentRound.songs.length) {
      // Round complete - go to scoring
      setState(prev => ({
        ...prev,
        currentRound: prev.currentRound ? {
          ...prev.currentRound,
          isComplete: true,
        } : null,
        isPlaying: false,
        showResults: true,
      }));
      return;
    }

    const nextSong = state.currentRound.songs[nextIndex];
    setState(prev => ({
      ...prev,
      currentRound: prev.currentRound ? {
        ...prev.currentRound,
        currentSongIndex: nextIndex,
      } : null,
      currentSong: nextSong,
      currentSongIndex: nextIndex,
      isPlaying: false,
    }));
  };

  const stopCurrentSong = () => {
    setState(prev => ({
      ...prev,
      isPlaying: false,
      isPaused: false,
    }));
  };

  const finishRound = () => {
    // Mark round as complete and go to scoring
    setState(prev => ({
      ...prev,
      currentRound: prev.currentRound ? {
        ...prev.currentRound,
        isComplete: true,
      } : null,
      isPlaying: false,
      showResults: true,
    }));
  };

  // Scoring (after round is complete)
  const updateSongAnswer = (teamId: string, songId: string, answer: Partial<SongAnswer>) => {
    setState(prev => {
      const teamAnswers = prev.roundAnswers[teamId] || {};
      const existingAnswer = teamAnswers[songId] || {
        songId,
        position: null,
        yearCorrect: false,
        artistCorrect: false,
        titleCorrect: false,
      };
      
      return {
        ...prev,
        roundAnswers: {
          ...prev.roundAnswers,
          [teamId]: {
            ...teamAnswers,
            [songId]: {
              ...existingAnswer,
              ...answer,
            },
          },
        },
      };
    });
  };

  const calculateRoundResults = () => {
    if (!state.currentRound) return;

    const updatedTeams = state.teams.map(team => {
      const teamAnswers = state.roundAnswers[team.id] || {};
      let newScore = team.score;
      let newTokens = team.tokens;
      let newTimeline = [...team.timeline];

      // Process each song in the round
      state.currentRound!.songs.forEach(song => {
        const answer = teamAnswers[song.id];
        if (!answer) return;

        // Convert to legacy format for calculateScore
        const legacyAnswer = {
          yearCorrect: answer.yearCorrect,
          artistCorrect: answer.artistCorrect,
          titleCorrect: answer.titleCorrect,
        };

        const result = calculateScore(
          { ...team, score: newScore, tokens: newTokens, timeline: newTimeline },
          legacyAnswer,
          song
        );

        newScore = result.newScore;
        if (result.earnedToken) {
          newTokens = Math.min(newTokens + 1, 5);
        }
        if (result.cardAdded) {
          newTimeline = insertCardInTimeline(newTimeline, {
            year: song.year,
            title: song.title,
            artist: song.artist,
          });
        }
      });

      return {
        ...team,
        score: newScore,
        tokens: newTokens,
        timeline: newTimeline,
      };
    });

    // Mark all round songs as played
    const playedSongIds = state.currentRound.songs.map(s => s.id);
    const updatedPlaylist = state.playlist.map(song =>
      playedSongIds.includes(song.id) ? { ...song, played: true } : song
    );

    setState(prev => ({
      ...prev,
      teams: updatedTeams,
      playlist: updatedPlaylist,
      showResults: true,
      currentRound: null,
    }));
  };

  // Legacy game actions (for backwards compatibility)
  const startRound = () => {
    if (!state.currentSong) return;

    setState(prev => ({
      ...prev,
      roundNumber: prev.roundNumber + 1,
      isPlaying: true,
      showResults: false,
    }));
  };

  const stopRound = () => {
    setState(prev => ({
      ...prev,
      isPlaying: false,
      isPaused: false,
    }));
  };

  const updateRoundAnswer = (teamId: string, answer: Partial<SongAnswer>) => {
    if (!state.currentSong) return;
    updateSongAnswer(teamId, state.currentSong.id, answer);
  };

  const calculateResults = () => {
    if (!state.currentSong) return;

    const updatedTeams = state.teams.map(team => {
      const teamAnswers = state.roundAnswers[team.id];
      if (!teamAnswers) return team;
      
      // Get the answer for the current song
      const answer = teamAnswers[state.currentSong!.id];
      if (!answer) return team;

      const legacyAnswer = {
        yearCorrect: answer.yearCorrect,
        artistCorrect: answer.artistCorrect,
        titleCorrect: answer.titleCorrect,
      };

      const result = calculateScore(team, legacyAnswer, state.currentSong!);

      let newTimeline = team.timeline;
      if (result.cardAdded) {
        newTimeline = insertCardInTimeline(team.timeline, {
          year: state.currentSong!.year,
          title: state.currentSong!.title,
          artist: state.currentSong!.artist,
        });
      }

      return {
        ...team,
        score: result.newScore,
        tokens: result.earnedToken ? team.tokens + 1 : team.tokens,
        timeline: newTimeline,
      };
    });

    // Mark current song as played
    const updatedPlaylist = state.playlist.map(song =>
      song.id === state.currentSong?.id ? { ...song, played: true } : song
    );

    setState(prev => ({
      ...prev,
      teams: updatedTeams,
      playlist: updatedPlaylist,
      showResults: true,
    }));
  };

  const nextRound = () => {
    setState(prev => ({
      ...prev,
      currentSong: null,
      currentSongIndex: null,
      roundAnswers: {},
      showResults: false,
    }));
  };

  const resetResults = () => {
    setState(prev => ({
      ...prev,
      showResults: false,
    }));
  };

  // Token management
  const spendToken = (teamId: string) => {
    setState(prev => ({
      ...prev,
      teams: prev.teams.map(team =>
        team.id === teamId && team.tokens > 0
          ? { ...team, tokens: team.tokens - 1 }
          : team
      ),
    }));
  };

  const earnToken = (teamId: string) => {
    setState(prev => ({
      ...prev,
      teams: prev.teams.map(team =>
        team.id === teamId && team.tokens < 5
          ? { ...team, tokens: team.tokens + 1 }
          : team
      ),
    }));
  };

  const buyCard = (teamId: string, year: number, title?: string, artist?: string) => {
    setState(prev => ({
      ...prev,
      teams: prev.teams.map(team => {
        if (team.id !== teamId || team.tokens < 3) return team;

        const newTimeline = insertCardInTimeline(team.timeline, {
          year,
          title,
          artist,
        });

        return {
          ...team,
          tokens: team.tokens - 3,
          timeline: newTimeline,
        };
      }),
    }));
  };

  // Audio control
  const setIsPlaying = (playing: boolean) => {
    setState(prev => ({ ...prev, isPlaying: playing }));
  };

  const setIsPaused = (paused: boolean) => {
    setState(prev => ({ ...prev, isPaused: paused }));
  };

  return (
    <GameContext.Provider
      value={{
        ...state,
        addTeam,
        removeTeam,
        updateTeam,
        clearTeams,
        addSong,
        removeSong,
        selectSong,
        updateSong,
        updateSongQuestions,
        clearPlaylist,
        startNewRound,
        playNextSong,
        stopCurrentSong,
        finishRound,
        updateSongAnswer,
        calculateRoundResults,
        startRound,
        stopRound,
        updateRoundAnswer,
        calculateResults,
        nextRound,
        resetResults,
        spendToken,
        earnToken,
        buyCard,
        setIsPlaying,
        setIsPaused,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within GameProvider');
  }
  return context;
};
